import { Platform } from 'react-native'

import {
  getPathFromState as defaultGetPathFromState,
  getStateFromPath,
} from '@react-navigation/native'

const linking = {
  prefixes: [
    'zetapp://',
    'https://app.onecode.in',
    'https://partner.zetapp.in',
    'http://partner.zetapp.in',
  ],
  getStateFromPath: (path: string, config: any) => {
    if (!path) return getStateFromPath('/', config)

    const [pathname] = path.split('?')

    if (pathname === '/digilocker-callback') {
      const newPath = '/bankAccount/add?fetchAadharAddress=true'
      return getStateFromPath(newPath, config)
    }

    return getStateFromPath(path, config)
  },
  getPathFromState: (
    state: Parameters<typeof defaultGetPathFromState>[0],
    config: Parameters<typeof defaultGetPathFromState>[1],
  ) => {
    const path = defaultGetPathFromState(state, config)

    // Web-only: Preserve hash and tag query params across navigation
    // Safe for mobile (Android/iOS) - window is undefined so this is skipped
    if (typeof window !== 'undefined' && Platform.OS === 'web') {
      const currentParams = new URLSearchParams(window?.location?.search)
      const hash = currentParams.get('hash')
      const tag = currentParams.get('tag')

      // Parse the new path to check if it already has query params
      const [pathname, existingQuery] = path.split('?')
      const newParams = new URLSearchParams(existingQuery || '')

      // Preserve hash and tag if they exist and aren't already in new path
      if (hash && !newParams.has('hash')) {
        newParams.set('hash', hash)
      }
      if (tag && !newParams.has('tag')) {
        newParams.set('tag', tag)
      }

      const finalQuery = newParams.toString()
      return finalQuery ? `${pathname}?${finalQuery}` : pathname
    }

    return path
  },
  config: {
    /* configuration for matching screens with paths */
    screens: {
      HomeView: {
        initialRouteName: 'Home',
        screens: {
          Home: 'home',
          Categories: 'categories',
          Grow: 'grow',
          Trainings: 'trainings',
          Leads: 'leads',
        },
      },
      LoggedIn: {
        initialRouteName: 'HomeView',
        screens: {
          MyIdCard: 'idCard',
          MyVisitingCard: 'visitingCard',
          AddBankAccount: 'bankAccount/add',
          PaymentModes: 'bankAccount/details',
          CreditScore: 'creditScore',
          Calculator: 'emiCalculator',
          EarningTutorial: 'howToEarn',
          CreditBuilderLoans: 'creditBuilderLoans',
          Earnings: {
            screens: {
              MyEarnings: 'earnings',
              History: 'earnings/history',
              BrandEarnings: 'earnings/byBrand',
              ChildEarnings: 'earnings/child',
            },
          },
          EnterNameAndPhoneNumber: 'customer/login/:bureauReportConsent?',
          UserLogin: 'user/login',
          UserOtp: 'user/otp',
          UserBasicDetails: 'user/basic-details',
          UserPanDetails: 'user/pan-details',
          UserOffers: 'user/offers',
          UserHome: 'user/home',
          VerifySecondaryPhoneNumber: 'customer/verifySecondaryPhoneNumber',
          VerifySecondaryPhoneOtp: 'customer/verifyOtp',
          LeadApplicationStatus: 'customer/applicationStatus',
          EnterNumberAndPan: 'customer/enterNumberAndPan',
          Vkyc: 'customer/vKYC',
          tracking: 'customer/applicationTracking',
          OtpScreen: 'customer/otp',
          BasicDetails: 'customer/basic',
          CustomerBasicDetailsV2: 'customer/basicv2',
          MixedBrandRecommendations: 'mixedBrandRecommendations',
          PanDetails: 'customer/panddetails',
          PanDetailsV2: 'customer/panddetailsv2',
          EmploymentDetailsWF: 'customer/employmentDetails',
          VerifyOtpAfterShare: 'customer/otpVerify/:bureauReportConsent?',
          ThankYouAfterVerify: 'customer/otpResult',
          BrandPage: 'category',
          BrandDetails: 'brand',
          InEligibleBrandsList: 'customer/inEligibleBrandsList',
          Profile: 'profile',
          Network: 'network',
          AdditionalDataPopup: 'customer/additionalDataPopup',
          HelpAndSupport: 'help',
          MyCustomers: 'customers',
          GuaranteedSales: 'preApproved',
          CustomerLead: 'customer/leads',
          LeadProgress: 'lead/details',
          ExclusiveOfferDetails: 'offer/exclusive',
          YourCertificates: 'trainings/certificates',
          VerifyPhone: 'login',
          ShareCardLink: 'link/share',
          BrandCustomers: 'brand/customers',
          EditCustomerDetails: 'customer/edit',
          KnowYourCustomer: 'add/customer',
          BrandBenefits: 'brand/benefits',
          BrandsHelpAndSupport: 'brand/help',
          EligibilityCriteria: 'brand/eligibilityCriteria',
          BrandTrainings: 'brand/trainings',
          ApplicationProcess: 'brand/applicationProcess',
          MarketingResource: 'brand/marketingResource',
          OfferLetter: 'offerLetter',
          MyTeamFaqs: 'myTeams/knowMore',
          BrandsCC: 'cc/brands',
          BrandsLOS: 'loans',
          OtpLessVerification: 'customer/otp/less/verification',
          LoyaltyProgramTierBenefits: 'tierBenefits',
          OtpAndCustomerDetailsWrapper: 'addCustomer/otp/details',
          CrossSelling: 'cross-selling',
          LoyaltyProgramLevelScreen: 'loyalty',
          GuaranteedBrandsList: 'guaranteed/brands',
          GuaranteedCustomerList: 'guaranteed/customers',
          CheckCreditScore: 'customer/check-credit-score',
          CreditReport: 'credit-report',
          SpotLightTasks: {
            screens: {
              PENDING: 'spotlight/pending',
              COMPLETED: 'spotlight/completed',
            },
          },
          recommendations: 'recommendations',
          GuaranteedLeadList: 'guaranteed/leads',
          AgentMarketingHome: 'marketing-tools',
          AgentMarketingCategoryList: 'marketing-tools/categories',
          LoyaltyGiftsAndRewards: 'rewards',
          LeaderBoard: 'leaderboard',
          Contest: 'contest',
          loyaltyCoinsLedger: 'coins',
          BrandTraining: 'training/live',
          ChatHistory: 'chat/history',
          // ZetStoreWebView: 'zetStore',
          AgentWebsiteInfoScreen: '/agent-website-info-screen',
          AgentWebsiteSetupScreen: '/agent-website-setup-screen',
          AgentWebsiteDashboard: '/agent-website-dashboard',
          AgentWebsiteFaqs: '/agent-website-faqs',
          BestOffers: 'best-offers',
          MyTeams: {
            screens: {
              Introduction: 'my-teams/introduction',
              Dashboard: 'my-teams/dashboard',
              AddTeam: 'my-teams/add-team',
              AddAdvisor: 'my-teams/add-advisor',
              AddAdmin: 'my-teams/add-admin',
            },
          },
          TotalEarningsScreen: 'total-earnings',
          VrmSupport: { screens: { vrmConverSation: 'chat/vrm-conversation' } },
          ProductPageWebflow: '/customer/product',
          ZetReferral: '/zetReferral',
          MagnetBenefits: '/magnet/benefits',
          IndependenceCampaign: 'independence-day-campaign',
          CCComparison: 'credit-card-comparison',
          ShoppingBenefits: 'shopping-benefits',
          BharatLoansMahotsavTeaser: 'bharat-loans-mahotsav-teaser',
          HowToEarnPoints: 'how-to-earn-points',
          PaytmBenefits: 'paytm-joining-benefits',
          QuickCustomerDetails: 'customer/add',
          OtpVerification: 'customer/verify/otp',
          QuickJourneyPanDetails: 'customer/add/pan',
          ZetStore: 'zet-store',
          MissingFieldsPopup: 'MissingFieldsPopup',
          BlmRewards: 'blm-rewards',
          WinnersList: 'winners-list',
          CustomerInfo: 'customer/info',
          CustomerBasicDetails: 'customer/basic/details',
          CustomerPanDetails: 'customer/pan/details',
          CustomerLoading: 'customer/jfs',
          DigiLockerCallback: 'digilocker-callback',
          ChoiceConnect: 'motor/insurance',
          CreditScoreReferralProgram: 'credit-score-referral-program',
        },
      },
    },
  },
}

export default linking
